import { ContentTypeCardLinkProps, ContentTypeCardWithPreviewProps } from './Types'
import { Pressable, useHover } from '@react-aria/interactions'
import cx from 'classnames'
import Styles from './ContentTypeCard.module.scss'
import { useFocusRing } from '@react-aria/focus'
import { mergeProps } from '@react-aria/utils'
import { Title } from 'screens/blaze/Home/components/Catalogue/components/CategoryCard/Title'
import { useCarouselPress } from 'components/Carousel/useCarouselPress'
import { Link } from 'react-router-dom'
import { forwardRef } from 'react'

export const ContentTypeCard = forwardRef<HTMLDivElement, ContentTypeCardWithPreviewProps>(
  (
    {
      isPressed,
      aspectRatio,
      title,
      preview: Preview,
      isFocusVisible,
      isHovered: propsIsHovered,
      icon: Icon,
      hideTitle = false,
      ...props
    },
    fRef,
  ) => {
    const { isHovered, hoverProps } = useHover(props)
    return (
      <div
        ref={fRef}
        className={cx(Styles.container, {
          [Styles.focusVisible]: isFocusVisible,
          [Styles.hovered]: isHovered || propsIsHovered,
          [Styles.pressed]: isPressed,
        })}
        {...mergeProps(hoverProps, props)}
      >
        <div
          style={{
            aspectRatio: `${aspectRatio}`,
          }}
          className={Styles.preview}
        >
          {Icon && (
            <div className={Styles.emoji}>
              <Icon size={26} />
            </div>
          )}
          <Preview isFocusVisible={isFocusVisible} isPressed={isPressed} isHovered={isHovered} />
        </div>

        {!hideTitle && <Title variant="smallList">{title}</Title>}
      </div>
    )
  },
)

export const PressableContentTypeCard = ({
  title,
  preview,
  aspectRatio,
  onPress,
  isHovered,
  isFocusVisible: propsIsFocusVisible,
  onFocusVisibleChange,
  ...props
}: ContentTypeCardWithPreviewProps) => {
  const { pressProps, isPressed, onTouchEnd, onTouchMove, onTouchStart } = useCarouselPress({ onPress, ...props })
  const { isFocusVisible, focusProps } = useFocusRing(props)

  return (
    <Pressable {...pressProps}>
      <ContentTypeCard
        tabIndex={0}
        role="button"
        aria-label={title}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        isPressed={isPressed}
        title={title}
        preview={preview}
        aspectRatio={aspectRatio}
        isFocusVisible={isFocusVisible || propsIsFocusVisible}
        isHovered={isHovered}
        {...focusProps}
      />
    </Pressable>
  )
}

export const ContentTypeCardLink = ({
  title,
  to,
  replace,
  component,
  preview,
  isHovered,
  isFocusVisible: propsIsFocusVisible,
  ...props
}: ContentTypeCardLinkProps) => {
  const { pressProps, isPressed, onTouchEnd, onTouchMove, onTouchStart } = useCarouselPress(props)
  const { isFocusVisible, focusProps } = useFocusRing(props)

  return (
    <Link
      to={to}
      replace={replace}
      component={component}
      className={Styles.link}
      aria-label={title}
      {...mergeProps(pressProps, focusProps)}
    >
      <ContentTypeCard
        title={title}
        preview={preview}
        isHovered={isHovered}
        {...props}
        isFocusVisible={isFocusVisible || propsIsFocusVisible}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        isPressed={isPressed}
      />
    </Link>
  )
}
